// ============================================================
// IOPIC — Formulaire de soumission de données / Data submission form
// NRCan · Inventaire des ouvrages de protection contre les inondations
// AECOM contract 60773284 — Task 4
// ------------------------------------------------------------
// Bilingual (FR default / EN). Text values use SurveyJS locale objects:
//   { default: "français", en: "english" }
// Switch the active language by setting survey.locale = 'fr' | 'en'.
//
// Choice VALUES are stable, language-independent strings so that
// visibleIf conditions and stored data never depend on the UI language.
//
// The licence sections (pages 'licence_droits', 'licence_permissions',
// 'licence_attribution_confidentialite', 'licence_existante') mirror the
// structure of "Generic Data Provider Authorization and Licence — Canada-
// wide Flood Defense Inventory" (Contract No. 3000810631): definitions,
// Grant of Rights (s.5), Permission Category (s.6.1–6.3), Attribution
// (s.9), Personal/Sensitive Information (s.11), Fees (s.13) and Existing
// Licences (s.7). Section 6.4 (Exceptional / Evaluation Only) is
// intentionally not asked — the form assumes the minimum rights are
// granted for the data to be usable in the project.
// ============================================================

// Reusable Oui / Non choice set
const YES_NO = [
  { value: 'oui', text: { default: 'Oui', en: 'Yes' } },
  { value: 'non', text: { default: 'Non', en: 'No' } }
]

// Presence of each attribute in the dataset (Section 5 matrix columns):
// Oui / Partiel / Non / Ne sais pas
const PRESENCE_COLUMNS = [
  { value: 'oui', text: { default: 'Oui', en: 'Yes' } },
  { value: 'partiel', text: { default: 'Partiel', en: 'Partial' } },
  { value: 'non', text: { default: 'Non', en: 'No' } },
  { value: 'ne_sais_pas', text: { default: 'Ne sais pas', en: "Don't know" } }
]

// Section 5 — database attribute inventory. Attribute NAMES are kept in
// English (they are the target data-model field names); the definitions
// are localized. Row text is rendered as HTML (see onTextMarkdown).
function attrRow (value, name, defFr, defEn) {
  return {
    value,
    text: {
      default: `<strong>${name}</strong><span class="attr-def">${defFr}</span>`,
      en: `<strong>${name}</strong><span class="attr-def">${defEn}</span>`
    }
  }
}

const ATTRIBUTE_ROWS = [
  attrRow('dike_name', 'Dike Name',
    "Identifiant unique pour une utilisation cohérente.",
    'Unique identifier for consistent usage.'),
  attrRow('location_xy', 'Location XY',
    "Emplacement exact de l'ouvrage (p. ex. sous forme de fichier de forme linéaire).",
    'Exact infrastructure location (e.g. as a line shapefile).'),
  attrRow('location_place', 'Location – Place Name',
    "Entité gouvernementale où se trouve la digue (municipalité, district régional ou province).",
    'Government area that the dike is located in (e.g. municipality, regional district, or province).'),
  attrRow('location_watershed', 'Location – Watershed',
    "Identification du bassin versant (p. ex. office de protection de la nature).",
    'Identification of the watershed (e.g. Conservation Authority).'),
  attrRow('location_protected', 'Location – Protected',
    "Identification de la communauté ou du quartier protégé.",
    'Identification of the protected community or neighbourhood.'),
  attrRow('design_watercourse', 'Design Watercourse',
    "Le cours d'eau contre lequel la digue protège.",
    'The watercourse that the dike protects from.'),
  attrRow('flood_type', 'Flood Type',
    "Côtier / rivière-ruisseau à eau claire / rivière-ruisseau à débris / etc.",
    'Coastal / clear water river-creek / debris river-creek / etc.'),
  attrRow('standard_protection', 'Standard of Protection',
    "Norme de protection, c.-à-d. probabilité annuelle de dépassement (AEP) de conception.",
    'Standard of protection, i.e. design AEP.'),
  attrRow('confidence_sop', 'Confidence in Standard of Protection',
    "Options possibles : certifiée par un ingénieur; conçue et modélisée par des travaux hydrotechniques; supposée à partir d'une analyse théorique; ou inconnue.",
    'Potential options include: certified by an engineer; designed and modelled through hydrotechnical work; assumed from desktop analysis; or unknown.'),
  attrRow('dike_owner', 'Dike Owner',
    "Partie ayant compétence sur l'ouvrage et responsable de l'entretien.",
    'Party with jurisdiction over the structure responsible for maintenance.'),
  attrRow('om_status', 'Operation and Maintenance Status',
    "Peut indiquer si les améliorations et l'entretien sont : inconnus; satisfaisants; à mettre à jour; etc.",
    'Could indicate whether upgrades and maintenance are unknown; satisfactory; in-need of upgrades; etc.'),
  attrRow('min_crest_elev', 'Minimum Crest Elevation',
    "Peut être enregistrée comme attribut représentant un minimum, ou rapportée le long de la digue en coordonnées Z par chaînage.",
    'Could be recorded as a particular attribute to represent a minimum or reported along the dike as chainage-based Z coordinates.'),
  attrRow('protected_area', 'Protected Area',
    "Couche spatiale supplémentaire (polygone) pour chaque digue.",
    'An additional spatial layer (polygon) for each dike.'),
  attrRow('fragility_curve', 'Dike-specific Fragility Curve',
    "Table de données supplémentaire pour chaque digue. Des courbes génériques appliquées à des digues précises peuvent aussi convenir.",
    'An additional data-table for each dike. Generic curves applied to specific dikes could also work.'),
  attrRow('year_constructed', 'Year Constructed',
    "Année de construction de la digue. Les dates d'améliorations importantes peuvent aussi être incluses.",
    'Year of dike construction. Significant upgrade dates could also be included.'),
  attrRow('date_data_update', 'Date of Data Update',
    "Date de mise à jour des données.",
    'Date that the data was updated.'),
  attrRow('maintenance_log', 'Maintenance Log',
    "Historique des inspections et notations d'inspection.",
    'Inspection history and inspection ratings.'),
  attrRow('condition_assessment', 'General Condition Assessment',
    "Évaluation globale de l'état physique de la digue selon les inspections (p. ex. bon, moyen, mauvais), tenant compte de l'intégrité structurale et de la performance.",
    "Overall evaluation of the dike's physical condition based on inspections or assessments (e.g. good, fair, poor), considering structural integrity and performance."),
  attrRow('risk_vulnerability', 'Flood Risk and Vulnerability',
    "Niveau de risque d'inondation de la zone protégée, incluant les conséquences potentielles d'une rupture et les facteurs de vulnérabilité (population, actifs, exposition).",
    'Level of flood risk associated with the protected area, including potential consequences of dike failure and factors influencing vulnerability (e.g. population, assets, exposure).'),
  attrRow('stakeholders', 'Dike Stakeholders',
    "Organisations, agences ou groupes clés impliqués dans la gestion, la réglementation ou l'usage de la digue.",
    'Key organizations, agencies, or groups involved in the management, regulation or use of the dike.'),
  attrRow('lifespan', 'Lifespan',
    "Durée de vie utile estimée (de conception ou résiduelle) de la structure, selon la conception d'origine, les matériaux et l'état.",
    'Estimated design life or remaining useful life of the dike structure, based on original design, materials, and condition assessments.'),
  attrRow('plan_availability', 'Plan Availability',
    "Plans ou documents pertinents (plans d'intervention d'urgence, plans d'entretien, rapports de conception), incluant s'ils sont à jour et accessibles.",
    'Relevant plans or documentation (e.g. emergency response plans, maintenance plans, design reports), including whether they are current and accessible.'),
  attrRow('other_devices', 'Other Operating Devices',
    "Pompes et stations de pompage, dispositifs anti-refoulement, chambres de vannes, stations hydrométriques.",
    'Pumps and pumping station, backflow prevention devices, valve chambers, hydrometric station.'),
  attrRow('certification', 'Certification',
    "Statut indiquant si la digue a été formellement revue, approuvée ou certifiée par une autorité qualifiée (ingénieur ou organisme réglementaire), incluant le type, l'organisme émetteur et la date, si disponibles.",
    'Status indicating whether the dike has been formally reviewed, approved, or certified by a qualified authority (e.g. professional engineer or regulatory body), including the type of certification, issuing organization, and date (if available).')
]

// ------------------------------------------------------------------
// Licence content — definitions, and the Grant of Rights explanations.
// Collapsed by default; the user opens them if/when needed.
// ------------------------------------------------------------------

const DEFINITIONS_HTML = {
  default: `<div class="licence-box">
    <dl>
      <dt>Données</dt>
      <dd>Les données géospatiales, bases de données, tableaux, dessins, photographies, rapports, documents, métadonnées et autre matériel que vous soumettez, dans la forme où vous les fournissez.</dd>
      <dt>Produits dérivés</dt>
      <dd>Analyses, compilations, extraits, visualisations, cartes, modèles, indicateurs et autres résultats produits à partir des données — mais pas une simple copie des données originales.</dd>
      <dt>Données harmonisées</dt>
      <dd>Données nettoyées, corrigées, converties, géoréférencées, reprojetées, reformatées, normalisées, combinées ou autrement traitées aux fins d'intégration à l'inventaire, tout en continuant de reproduire des éléments substantiels des données originales.</dd>
      <dt>Inventaire</dt>
      <dd>L'inventaire pancanadien des ouvrages de protection contre les inondations, y compris ses mises à jour et versions futures.</dd>
      <dt>Métadonnées</dt>
      <dd>Informations décrivant les données : source, propriétaire ou responsable, date, licence, attribution, qualité, fiabilité et limites de diffusion ou d'utilisation.</dd>
      <dt>Extrants du projet</dt>
      <dd>Bases de données, modèles, schémas, données harmonisées, produits dérivés, code, documentation, rapports et autres éléments créés par AECOM dans le cadre du contrat.</dd>
      <dt>Renseignements personnels</dt>
      <dd>Tout renseignement concernant un individu identifiable, au sens des lois applicables en matière de protection des renseignements personnels.</dd>
      <dt>Renseignements sensibles</dt>
      <dd>Renseignements jugés sensibles sur le plan culturel, confidentiels, propriétaires, contrôlés, liés à la sécurité, protégés ou autrement soumis à des exigences particulières d'accès, d'utilisation, de divulgation ou de conservation.</dd>
    </dl>
  </div>`,
  en: `<div class="licence-box">
    <dl>
      <dt>Data</dt>
      <dd>The geospatial data, databases, tabular records, drawings, photographs, reports, documents, metadata and related material you submit, in the form you supply them.</dd>
      <dt>Derived Products</dt>
      <dd>Analyses, compilations, extracts, visualizations, maps, models, indicators and other outputs generated using the data — but not a mere copy of the original data.</dd>
      <dt>Harmonized Data</dt>
      <dd>Data cleaned, corrected, converted, georeferenced, reprojected, reformatted, standardized, combined or otherwise processed for integration into the inventory, while continuing to reproduce material elements of the original data.</dd>
      <dt>Inventory</dt>
      <dd>The Canada-wide Flood Defense Inventory, including any continuation, update or future version.</dd>
      <dt>Metadata</dt>
      <dd>Information describing the data: source, owner or custodian, date, licence, attribution, quality, reliability and distribution or handling limitations.</dd>
      <dt>Project Outputs</dt>
      <dd>Databases, models, schemas, harmonized data, derived products, code, documentation, reports and other outputs created by AECOM in performing the contract.</dd>
      <dt>Personal Information</dt>
      <dd>Information about an identifiable individual within the meaning of the applicable privacy law.</dd>
      <dt>Sensitive Information</dt>
      <dd>Information identified as culturally sensitive, confidential, proprietary, controlled, security-sensitive, protected or otherwise subject to special access, use, disclosure or handling requirements.</dd>
    </dl>
  </div>`
}

const AECOM_RIGHTS_HTML = {
  default: `<div class="licence-box">
    <p>Pour la durée du contrat entre AECOM et RNCan seulement (actuellement jusqu'au 31 mars 2027), et uniquement aux fins de ce contrat, AECOM peut recevoir, copier, corriger, harmoniser et intégrer vos données à l'inventaire, en créer des données harmonisées et des produits dérivés, et les transmettre au Canada.</p>
    <p><strong>AECOM utilisera vos données uniquement aux fins du projet, pour la durée du contrat, et ne les utilisera à aucune autre fin.</strong> Ces droits prennent fin avec le contrat; ils ne s'étendent à aucun autre usage ni à aucune autre partie.</p>
  </div>`,
  en: `<div class="licence-box">
    <p>For the duration of the contract between AECOM and NRCan only (currently to March 31, 2027), and solely for the purposes of that contract, AECOM may receive, copy, correct, harmonize and integrate your data into the inventory, create harmonized data and derived products from it, and deliver these to Canada.</p>
    <p><strong>AECOM will use your data solely for the purpose of the project, for the duration of the contract, and will not use it for any other purpose.</strong> These rights end with the contract; they do not extend to any other use or any other party.</p>
  </div>`
}

const CANADA_RIGHTS_HTML = {
  default: `<div class="licence-box">
    <p>Le Canada (représenté par Ressources naturelles Canada) reçoit directement de votre part une licence non exclusive, perpétuelle, irrévocable, mondiale et libre de redevances, lui permettant de conserver, reproduire, corriger, harmoniser, combiner et utiliser vos données — pendant et après la fin du contrat d'AECOM — à des fins gouvernementales légitimes (cartographie des inondations, gestion des urgences, recherche, planification, politiques publiques, information publique), et de les partager avec de futurs fournisseurs de services chargés de maintenir l'inventaire.</p>
    <p>Le Canada peut également partager les données avec des entrepreneurs travaillant pour le Canada et pour d'autres gouvernements, à des fins d'information, et peut créer et posséder des extrants du projet dérivés des données. Ces droits n'autorisent pas la diffusion publique, sauf dans la mesure permise ci-dessous.</p>
    <p>Ces droits minimaux sont nécessaires pour que le Canada puisse pleinement utiliser l'inventaire. <strong>Sans eux, vos données ne peuvent pas être intégrées à l'inventaire</strong> (voir la section Permissions ci-après).</p>
  </div>`,
  en: `<div class="licence-box">
    <p>Canada (represented by Natural Resources Canada) receives directly from you a non-exclusive, perpetual, irrevocable, worldwide and royalty-free licence allowing it to retain, reproduce, correct, harmonize, combine and use your data — during and after the end of AECOM's contract — for lawful governmental purposes (flood mapping, emergency management, research, planning, public policy, public information), and to share it with future service providers responsible for maintaining the inventory.</p>
    <p>Canada may also share the data with contractors working for Canada and other governments for information purposes, and may create and own project outputs derived from the data. These rights do not authorize public release unless permitted below.</p>
    <p>These minimum rights are required for Canada to make full use of the inventory. <strong>Without them, your data cannot be incorporated into the inventory</strong> (see the Permissions section below).</p>
  </div>`
}

export const surveyJson = {
  title: {
    default: "Formulaire de soumission de données — Ouvrages de protection contre les inondations",
    en: 'Data submission form — Flood protection systems'
  },
  description: {
    default: "Inventaire pancanadien des ouvrages de protection contre les inondations — Ressources naturelles Canada",
    en: 'Canada-wide Inventory of Flood Protection Systems — Natural Resources Canada'
  },
  logoPosition: 'right',
  showProgressBar: 'top',
  progressBarType: 'pages',
  showQuestionNumbers: 'off',
  checkErrorsMode: 'onValueChanged',
  widthMode: 'responsive',
  firstPageIsStarted: false,
  completeText: { default: 'Continuer vers le téléversement', en: 'Continue to file upload' },
  pageNextText: { default: 'Suivant', en: 'Next' },
  pagePrevText: { default: 'Précédent', en: 'Previous' },
  requiredText: '*',

  pages: [
    // ------------------------------------------------------------------
    // PAGE 1 — Identification & contact (Section 0)
    // ------------------------------------------------------------------
    {
      name: 'identification',
      title: { default: 'Identification', en: 'Identification' },
      elements: [
        {
          type: 'html',
          name: 'intro',
          html: {
            default: `<div class="survey-intro">
              <h4>Mise en contexte</h4>
              <p>Ce formulaire permet de soumettre des données existantes sur les <strong>ouvrages de protection contre les inondations</strong> (digues, murs de protection, canaux de dérivation) en vue de leur intégration à l'inventaire pancanadien mené par AECOM sous contrat avec Ressources naturelles Canada.</p>
              <p>Vos réponses sont enregistrées à chaque page. Vous pourrez téléverser vos fichiers (jeu de données, dictionnaire de données, documents pertinents) à la dernière étape.</p>
              <p><strong>Un seul type de données par soumission.</strong> Si vous possédez plusieurs types de données, veuillez remplir le formulaire une fois par type.</p>
            </div>`,
            en: `<div class="survey-intro">
              <h4>Context</h4>
              <p>This form is used to collect existing data on <strong>flood protection systems</strong> (including levees, dikes, floodwalls and other engineered structures) for integration into the Canada-wide inventory, which is managed by AECOM under contract with Natural Resources Canada.</p>
              <p>Your answers are saved at every page. You will upload your files (dataset, data dictionary, relevant documents) at the last step.</p>
              <p><strong>One data type per submission.</strong> If you have several data types, please complete the form once per type.</p>
            </div>`
          }
        },
        {
          type: 'text',
          name: 'identification_code',
          title: {
            default: "Code d'accès (facultatif)",
            en: 'Access code (optional)'
          },
          description: {
            default: "Si un code de 6 caractères vous a été transmis (dans le document de soumission ou le lien reçu), entrez-le ici. Il permet d'associer votre soumission à votre organisation. Sinon, laissez ce champ vide.",
            en: 'If you received a 6-character code (in the submission document or the link you were sent), enter it here. It links your submission to your organization. Otherwise, leave this field blank.'
          },
          isRequired: false,
          textUpdateMode: 'onTyping',
          placeholder: 'ABC123',
          maxLength: 6,
          validators: [
            {
              type: 'regex',
              regex: '^$|^[A-Za-z0-9]{6}$',
              text: {
                default: "Le code d'accès doit contenir exactement 6 caractères (lettres ou chiffres).",
                en: 'The access code must contain exactly 6 characters (letters or digits).'
              }
            }
          ]
        },
        {
          type: 'html',
          name: 'organisation_details',
          html: '',
          visible: false
        },
        {
          type: 'boolean',
          name: 'is_resubmission',
          visible: false,
          defaultValue: false
        },
        {
          type: 'html',
          name: 'resubmission_notice',
          visibleIf: '{is_resubmission} = true',
          html: {
            default: '<div class="notice-warn">Une soumission a déjà été enregistrée pour cette organisation. Une nouvelle soumission s\'ajoutera aux données déjà reçues.</div>',
            en: '<div class="notice-warn">A submission has already been recorded for this organization. A new submission will be added to the data already received.</div>'
          }
        },
        {
          type: 'text',
          name: 'contact_name',
          title: {
            default: 'Nom de la personne-ressource (contributeur de données)',
            en: 'Contact person (data contributor)'
          },
          isRequired: true
        },
        {
          type: 'text',
          name: 'contact_email',
          title: { default: 'Adresse courriel', en: 'Email address' },
          isRequired: true,
          inputType: 'email',
          validators: [{ type: 'email' }]
        },
        {
          type: 'text',
          name: 'organisation_name',
          title: {
            default: "Nom de l'organisation",
            en: 'Organization name'
          },
          isRequired: true
        },
      ]
    },

    // ------------------------------------------------------------------
    // PAGE 3 — Licence : définitions et droits accordés (Section 2 & 5)
    // ------------------------------------------------------------------
    {
      name: 'licence_droits',
      title: { default: 'Licence — Droits accordés', en: 'Licence — Grant of rights' },
      description: {
        default: "Cette section explique, en termes simples, les droits qu'AECOM et le Canada doivent obtenir sur vos données pour les intégrer à l'inventaire. Elle reprend la logique du document légal d'autorisation de données utilisé pour ce projet.",
        en: 'This section explains, in plain terms, the rights AECOM and Canada need over your data to incorporate it into the inventory. It follows the logic of the legal data authorization document used for this project.'
      },
      elements: [
        {
          type: 'panel',
          name: 'definitions_panel',
          state: 'collapsed',
          title: { default: 'Définitions utiles (cliquez pour afficher)', en: 'Useful definitions (click to expand)' },
          elements: [
            {
              type: 'html',
              name: 'definitions_html',
              titleLocation: 'hidden',
              html: DEFINITIONS_HTML
            }
          ]
        },
        {
          type: 'panel',
          name: 'aecom_rights_panel',
          title: { default: 'Droits accordés à AECOM (pour la durée du contrat seulement)', en: "Rights granted to AECOM (for the contract duration only)" },
          elements: [
            {
              type: 'html',
              name: 'aecom_rights_html',
              titleLocation: 'hidden',
              html: AECOM_RIGHTS_HTML
            }
          ]
        },
        {
          type: 'panel',
          name: 'canada_rights_panel',
          title: { default: 'Droits minimaux accordés au Canada', en: 'Minimum rights granted to Canada' },
          elements: [
            {
              type: 'html',
              name: 'canada_rights_html',
              titleLocation: 'hidden',
              html: CANADA_RIGHTS_HTML
            }
          ]
        },
        {
          type: 'radiogroup',
          name: 'grant_minimum_rights',
          title: {
            default: "Accordez-vous à AECOM et au Canada les droits minimaux décrits ci-dessus (« Droits minimaux accordés au Canada ») ?",
            en: 'Do you grant AECOM and Canada the minimum rights described above ("Minimum rights granted to Canada")?'
          },
          description: {
            default: "Requis pour intégrer vos données à un livrable du contrat.",
            en: 'Required to incorporate your data into a contract deliverable.'
          },
          isRequired: true,
          colCount: 0,
          choices: YES_NO
        },
        {
          type: 'html',
          name: 'minimum_rights_refusal_notice',
          visibleIf: "{grant_minimum_rights} = 'non'",
          html: {
            default: '<div class="notice-warn">Si vous n\'accordez pas ces droits minimaux, nous ne pourrons pas recevoir votre soumission dans le cadre du contrat actuel. Veuillez expliquer pourquoi dans la case ci-dessous, puis cliquez sur « Continuer ». Notre équipe communiquera avec vous pour discuter des enjeux soulevés.</div>',
            en: "<div class=\"notice-warn\">If you do not grant these minimum rights, we cannot receive your submission under the current contract. Please explain why in the box below, then click \"Continue\". Our team will contact you to discuss the concerns you raised.</div>"
          }
        },
        {
          type: 'comment',
          name: 'minimum_rights_refusal_reason',
          visibleIf: "{grant_minimum_rights} = 'non'",
          isRequired: true,
          title: {
            default: "Veuillez expliquer pourquoi vous ne pouvez pas accorder ces droits minimaux",
            en: 'Please explain why you cannot grant these minimum rights'
          }
        }
      ]
    },

    // ------------------------------------------------------------------
    // PAGE 4 — Licence : permissions (Section 6.1 – 6.3)
    // Skipped entirely if the minimum rights above were refused — the
    // survey ends right after the concerns are explained.
    // ------------------------------------------------------------------
    {
      name: 'licence_permissions',
      title: { default: 'Licence — Permissions', en: 'Licence — Permissions' },
      visibleIf: "{grant_minimum_rights} <> 'non'",
      elements: [
        {
          type: 'html',
          name: 'permissions_intro_notice',
          html: {
            default: "<div class=\"notice-info\">Cette section vise à déterminer ce qu'il sera possible de faire avec vos données une fois intégrées à l'inventaire (diffusion, partage, réutilisation). Elle n'est pas obligatoire dans le cadre du contrat actuel. Si vous n'autorisez pas la diffusion ouverte, des choix de partage plus restreints vous seront proposés.</div>",
            en: '<div class="notice-info">This section is meant to determine what can be done with your data once it is incorporated into the inventory (release, sharing, reuse). It is not mandatory under the current contract. If you do not authorize open release, you will be presented with more restricted sharing choices.</div>'
          }
        },
        {
          type: 'radiogroup',
          name: 'authorize_open_use',
          title: {
            default: "Autorisez-vous la diffusion ouverte de vos données (données originales, données harmonisées et produits dérivés) — incluant leur modification, combinaison, partage et redistribution publique, y compris à des fins commerciales — sous la Licence du gouvernement ouvert du Canada ?",
            en: 'Do you authorize the open release of your data (original data, harmonized data and derived products) — including its modification, combination, sharing and public redistribution, including for commercial purposes — under the Open Government Licence – Canada?'
          },
          description: {
            default: 'Licence du gouvernement ouvert – Canada : https://open.canada.ca/en/open-government-licence-canada',
            en: 'Open Government Licence – Canada: https://open.canada.ca/en/open-government-licence-canada'
          },
          isRequired: true,
          colCount: 0,
          choices: YES_NO
        },
        {
          type: 'checkbox',
          name: 'restricted_sharing_scope',
          visibleIf: "{grant_minimum_rights} = 'oui' and {authorize_open_use} = 'non'",
          title: {
            default: "Puisque la diffusion ouverte n'est pas autorisée, précisez la portée de partage et de diffusion que vous autorisez (cochez toutes les réponses qui s'appliquent) :",
            en: 'Since open release is not authorized, please specify the sharing and release scope you authorize (check all that apply):'
          },
          isRequired: false,
          colCount: 1,
          choices: [
            {
              value: 'share_gc',
              text: { default: 'Partage au sein du gouvernement du Canada (autres ministères fédéraux)', en: 'Sharing within the Government of Canada (other federal departments)' }
            },
            {
              value: 'share_successors',
              text: { default: "Partage avec les futurs fournisseurs de services chargés de maintenir l'inventaire", en: 'Sharing with future service providers responsible for maintaining the inventory' }
            },
            {
              value: 'release_harmonized',
              text: { default: 'Diffusion publique des données harmonisées autorisée', en: 'Public release of harmonized data is authorized' }
            },
            {
              value: 'release_derived',
              text: { default: 'Diffusion publique des produits dérivés autorisée', en: 'Public release of derived products is authorized' }
            },
            {
              value: 'release_aggregated_only',
              text: { default: 'Diffusion publique sous forme agrégée, généralisée ou anonymisée', en: 'Public release in aggregated, generalized or anonymized form' }
            },
          ]
        }
      ]
    },

    // ------------------------------------------------------------------
    // PAGE 5 — Attribution, confidentialité et frais (Sections 9, 11, 13)
    // ------------------------------------------------------------------
    {
      name: 'licence_attribution_confidentialite',
      title: { default: 'Attribution, confidentialité et frais', en: 'Attribution, confidentiality and fees' },
      visibleIf: "{grant_minimum_rights} <> 'non'",
      elements: [
        {
          type: 'radiogroup',
          name: 'attribution_required',
          title: {
            default: "Souhaitez-vous qu'une attribution ou une citation particulière soit utilisée lorsque vos données (ou les produits qui en découlent) sont diffusées ?",
            en: 'Do you want a specific attribution or citation to be used when your data (or products derived from it) is shared?'
          },
          description: {
            default: "Si oui, veuillez fournir l'attribution souhaitée ci-dessous.",
            en: 'If yes, please provide the preferred attribution below.'
          },
          isRequired: true,
          colCount: 0,
          choices: YES_NO
        },
        {
          type: 'comment',
          name: 'attribution_text',
          visibleIf: "{attribution_required} = 'oui'",
          isRequired: true,
          title: {
            default: 'Attribution ou citation exacte à utiliser',
            en: 'Exact attribution or citation to use'
          }
        },
        {
          type: 'checkbox',
          name: 'no_personal_info',
          titleLocation: 'hidden',
          choices: [
            {
              value: 'confirmed',
              text: {
                default: 'Je confirme que les données soumises ne contiennent aucun renseignement personnel (voir la définition à la section précédente).',
                en: 'I confirm that the submitted data does not contain any personal information (see the definition on the previous page).'
              }
            }
          ]
        },
        {
          type: 'html',
          name: 'personal_info_notice',
          visibleIf: "{no_personal_info} notcontains 'confirmed'",
          html: {
            default: '<div class="notice-warn">Votre soumission pourrait contenir des renseignements personnels. Veuillez le préciser ci-dessous. <strong>Veuillez ne pas téléverser les données à cette étape.</strong> AECOM communiquera avec vous pour discuter des prochaines étapes.</div>',
            en: '<div class="notice-warn">Your submission may contain personal information. Please provide details below. <strong>Please do not upload the data at this stage.</strong> AECOM will contact you to discuss next steps.</div>'
          }
        },
        {
          type: 'comment',
          name: 'personal_info_justification',
          visibleIf: "{no_personal_info} notcontains 'confirmed'",
          isRequired: true,
          title: {
            default: 'Veuillez préciser quels renseignements personnels sont inclus et pourquoi',
            en: 'Please specify what personal information is included and why'
          }
        },
        {
          type: 'checkbox',
          name: 'no_sensitive_info',
          titleLocation: 'hidden',
          choices: [
            {
              value: 'confirmed',
              text: {
                default: 'Je confirme que les données soumises ne contiennent aucune information confidentielle ou sensible (culturellement sensible, propriétaire, protégée ou autrement contrôlée — voir la définition à la section précédente).',
                en: 'I confirm that the submitted data does not contain any confidential or sensitive information (culturally sensitive, proprietary, protected or otherwise controlled — see the definition on the previous page).'
              }
            }
          ]
        },
        {
          type: 'html',
          name: 'sensitive_info_notice',
          visibleIf: "{no_sensitive_info} notcontains 'confirmed'",
          html: {
            default: '<div class="notice-warn">Votre soumission pourrait contenir de l\'information confidentielle ou sensible. Veuillez le préciser ci-dessous. <strong>Veuillez ne pas téléverser les données à cette étape.</strong> AECOM communiquera avec vous pour discuter des prochaines étapes.</div>',
            en: '<div class="notice-warn">Your submission may contain confidential or sensitive information. Please provide details below. <strong>Please do not upload the data at this stage.</strong> AECOM will contact you to discuss next steps.</div>'
          }
        },
        {
          type: 'comment',
          name: 'sensitive_info_justification',
          visibleIf: "{no_sensitive_info} notcontains 'confirmed'",
          isRequired: true,
          title: {
            default: 'Veuillez préciser quelle information confidentielle ou sensible est incluse et pourquoi',
            en: 'Please specify what confidential or sensitive information is included and why'
          }
        },
        {
          type: 'checkbox',
          name: 'fee_free_confirmation',
          titleLocation: 'hidden',
          isRequired: true,
          requiredErrorText: {
            default: 'Vous devez confirmer que les données sont fournies sans frais pour poursuivre.',
            en: 'You must confirm that the data is provided free of charge to continue.'
          },
          choices: [
            {
              value: 'confirmed',
              text: {
                default: 'Je confirme que les données sont fournies sans frais, redevance ni autre forme de contrepartie.',
                en: 'I confirm that the data is provided without fee, royalty or other charge.'
              }
            }
          ]
        }
      ]
    },

    // ------------------------------------------------------------------
    // PAGE 6 — Licence existante et confirmation (Section 7)
    // ------------------------------------------------------------------
    {
      name: 'licence_existante',
      title: { default: 'Licence existante et confirmation', en: 'Existing licence and confirmation' },
      visibleIf: "{grant_minimum_rights} <> 'non'",
      elements: [
        {
          type: 'radiogroup',
          name: 'has_existing_licence',
          title: {
            default: 'Vos données possèdent-elles une licence existante ?',
            en: 'Does your data have an existing licence?'
          },
          isRequired: true,
          colCount: 0,
          choices: YES_NO
        },
        {
          type: 'html',
          name: 'existing_licence_upload_note',
          visibleIf: "{has_existing_licence} = 'oui'",
          html: {
            default: '<div class="notice-info">Vous pourrez <strong>téléverser votre licence existante</strong> à la dernière étape (téléversement des fichiers), avec le jeu de données.</div>',
            en: '<div class="notice-info">You will be able to <strong>upload your existing licence</strong> at the last step (file upload), together with the dataset.</div>'
          }
        },
        {
          type: 'comment',
          name: 'other_conditions',
          title: {
            default: 'Autres conditions ou restrictions non couvertes ci-dessus (le cas échéant)',
            en: 'Other conditions or restrictions not covered above (if any)'
          },
          description: {
            default: "Toute condition indiquée ici sera examinée avant l'intégration des données à l'inventaire.",
            en: 'Any conditions identified here are subject to review before the data is incorporated into the inventory.'
          },
          isRequired: false
        },
        {
          type: 'checkbox',
          name: 'authorization_confirmation',
          titleLocation: 'hidden',
          isRequired: true,
          requiredErrorText: {
            default: "Vous devez confirmer cette déclaration pour poursuivre.",
            en: 'You must confirm this statement to continue.'
          },
          choices: [
            {
              value: 'confirmed',
              text: {
                default: "Je confirme être autorisé à soumettre ces données au nom de mon organisation et que mon organisation détient les droits nécessaires pour fournir les données et accorder les permissions ci-dessus.",
                en: 'I confirm that I am authorized to submit this data on behalf of my organization and that my organization has the right to provide the data and grant the permissions above.'
              }
            }
          ]
        },
        {
          type: 'text',
          name: 'signature_full_name',
          title: {
            default: 'Signature électronique (nom complet, dactylographié)',
            en: 'Electronic signature (typed full name)'
          },
          isRequired: true
        },
        {
          type: 'text',
          name: 'signature_title',
          title: {
            default: 'Titre / fonction',
            en: 'Title / role'
          },
          isRequired: false
        },
        {
          type: 'text',
          name: 'signature_date',
          inputType: 'date',
          title: { default: 'Date', en: 'Date' },
          readOnly: true,
          defaultValueExpression: 'today()'
        }
      ]
    },

    // ------------------------------------------------------------------
    // PAGE 7 — Type de données (Section 4)
    // ------------------------------------------------------------------
    {
      name: 'type_donnees',
      title: { default: 'Type de données', en: 'Data type' },
      visibleIf: "{grant_minimum_rights} <> 'non'",
      elements: [
        {
          type: 'text',
          name: 'dataset_name',
          title: {
            default: 'Nom du jeu de données',
            en: 'Dataset name'
          },
          description: {
            default: "Nom permettant d'identifier ce jeu de données (p. ex. le nom du fichier ou un titre court).",
            en: 'A name to identify this dataset (e.g. the file name or a short title).'
          },
          isRequired: true
        },
        {
          type: 'comment',
          name: 'dataset_description',
          title: {
            default: 'Description du jeu de données (facultatif)',
            en: 'Dataset description (optional)'
          },
          description: {
            default: 'Décrivez brièvement le contenu, la portée ou le contexte de ce jeu de données.',
            en: 'Briefly describe the content, scope or context of this dataset.'
          },
          isRequired: false
        },
        {
          type: 'radiogroup',
          name: 'data_type',
          title: {
            default: 'Quel type de données est en votre possession ?',
            en: 'What type of data do you have?'
          },
          isRequired: true,
          choices: [
            {
              value: 'type1',
              text: {
                default: 'Type 1 — Données vectorielles SIG (shapefile ou géodatabase) avec attributs complets et métadonnées intégrées.',
                en: 'Type 1 — GIS vector data (shapefile or geodatabase) with complete attributes and integrated metadata.'
              }
            },
            {
              value: 'type2',
              text: {
                default: "Type 2 — Données vectorielles (shapefiles ou géodatabases) dont les métadonnées ou dictionnaires d'attributs sont fournis séparément.",
                en: 'Type 2 — GIS vector data (shapefiles or geodatabases) with metadata or attribute dictionaries supplied separately.'
              }
            },
            {
              value: 'type3',
              text: {
                default: 'Type 3 — Données raster géoréférencées (imagerie, modèles numériques de terrain, orthophotos, etc.).',
                en: 'Type 3 — Georeferenced raster data (imagery, digital elevation models, orthophotos, etc.).'
              }
            },
            {
              value: 'type4',
              text: {
                default: 'Type 4 — Données non géoréférencées : plans CAO, copies papier ou cartes intégrées aux rapports techniques.',
                en: 'Type 4 — Non-georeferenced data: CAD plans, paper copies or maps embedded in technical reports.'
              }
            },
            {
              value: 'type5',
              text: {
                default: 'Type 5 — Données cartographiques sur support papier ou images numérisées (cartes, annexes de rapports, croquis techniques).',
                en: 'Type 5 — Cartographic data on paper or scanned images (maps, report appendices, technical sketches).'
              }
            },
            {
              value: 'type6',
              text: {
                default: 'Type 6 — Liens vers des services Web géospatiaux (WMS, WFS, GeoRSS ou API équivalentes).',
                en: 'Type 6 — Links to geospatial web services (WMS, WFS, GeoRSS or equivalent APIs).'
              }
            }
          ]
        },
        {
          type: 'comment',
          name: 'web_service_links',
          visibleIf: "{data_type} = 'type6'",
          title: {
            default: 'Indiquez le ou les liens vers les services Web géospatiaux (une URL par ligne).',
            en: 'Provide the link(s) to the geospatial web services (one URL per line).'
          },
          isRequired: true
        },
        {
          type: 'html',
          name: 'data_type_note',
          html: {
            default: '<div class="notice-info">Note : si vous avez plus d\'un type de données, veuillez faire une soumission supplémentaire pour chaque type.</div>',
            en: '<div class="notice-info">Note: if you have more than one data type, please make an additional submission for each type.</div>'
          }
        }
      ]
    },

    // ------------------------------------------------------------------
    // PAGE 8 — Inventaire des attributs (Section 5)
    // ------------------------------------------------------------------
    {
      name: 'attributs',
      title: { default: 'Informations présentes dans les données', en: 'Information present in the data' },
      visibleIf: "{grant_minimum_rights} <> 'non'",
      description: {
        default: 'Pour chaque attribut, indiquez s\'il est présent : Oui, Non ou Ne sais pas.',
        en: 'For each attribute, indicate whether it is present: Yes, No, or Don\'t know.'
      },
      elements: [
        {
          type: 'matrix',
          name: 'attribute_inventory',
          titleLocation: 'hidden',
          isAllRowRequired: false,
          columns: PRESENCE_COLUMNS,
          rows: ATTRIBUTE_ROWS
        }
      ]
    },

    // ------------------------------------------------------------------
    // PAGE 9 — Commentaires additionnels (générique)
    // ------------------------------------------------------------------
    {
      name: 'commentaires',
      title: { default: 'Commentaires additionnels', en: 'Additional comments' },
      visibleIf: "{grant_minimum_rights} <> 'non'",
      description: {
        default: "Facultatif. Ajoutez tout commentaire ou précision utile à la compréhension de vos données ou de votre soumission.",
        en: 'Optional. Add any comment or clarification useful to understanding your data or your submission.'
      },
      elements: [
        {
          type: 'comment',
          name: 'additional_comments',
          title: {
            default: 'Tout commentaire additionnel',
            en: 'Any additional comment'
          },
          description: {
            default: "Par exemple : contexte particulier, limites connues des données, personne à contacter, ou toute autre information pertinente.",
            en: 'For example: specific context, known data limitations, a person to contact, or any other relevant information.'
          },
          isRequired: false,
          rows: 6,
          autoGrow: true
        }
      ]
    },

    // ------------------------------------------------------------------
    // PAGE 10 — Finalisation (leads into file-upload phase)
    // ------------------------------------------------------------------
    {
      name: 'finalisation',
      title: { default: 'Dernière étape — Téléversement des fichiers', en: 'Last step — File upload' },
      visibleIf: "{grant_minimum_rights} <> 'non'",
      elements: [
        {
          type: 'html',
          name: 'upload_intro',
          html: {
            default: `<div class="survey-intro">
              <p>Vous avez répondu à toutes les questions. À l'étape suivante, vous pourrez <strong>téléverser vos fichiers</strong> :</p>
              <ul>
                <li>le jeu de données géospatiales (SIG, CAO, raster, CSV ou copies numérisées);</li>
                <li>le <strong>dictionnaire de données</strong> et tout document pertinent à la compréhension des données;</li>
                <li>votre <strong>licence existante</strong>, le cas échéant.</li>
              </ul>
              <p>Cliquez sur « Continuer vers le téléversement » pour poursuivre.</p>
            </div>`,
            en: `<div class="survey-intro">
              <p>You have answered all the questions. In the next step, you will be able to <strong>upload your files</strong>:</p>
              <ul>
                <li>the geospatial dataset (GIS, CAD, raster, CSV or scanned copies);</li>
                <li>the <strong>data dictionary</strong> and any document relevant to understanding the data;</li>
                <li>your <strong>existing licence</strong>, if applicable.</li>
              </ul>
              <p>Click "Continue to file upload" to proceed.</p>
            </div>`
          }
        }
      ]
    }
  ]
}
